/**
 * API service for global search.
 *
 * The response types are generated from the backend Pydantic `SearchResponse`
 * model (JSON-schema-first / generated-types contract) and re-exported here so
 * `searchApi` stays the single import surface for search consumers. Do not
 * hand-maintain response interfaces — edit the Pydantic models in
 * `shared/search/api/routes.py` and re-run `npm run generate:api`.
 */
import { apiGet } from '../api/axiosInstance';
import type { SearchEntityResult } from '@/generated/api/models/searchEntityResult';
import type { SearchFacet } from '@/generated/api/models/searchFacet';
import type { SearchPageResult } from '@/generated/api/models/searchPageResult';
import type { SearchResponse } from '@/generated/api/models/searchResponse';
import type { SearchTagResult } from '@/generated/api/models/searchTagResult';

export type {
  SearchResponse,
  SearchPageResult,
  SearchEntityResult,
  SearchTagResult,
  SearchFacet,
};

/** Request-side filter state used to build the query string (not a response shape). */
export interface SearchFilters {
  modules: string[];
  entityTypes: string[];
  dateRange: { from?: string; to?: string } | null;
}

export const searchApi = {
  search: async (
    query: string,
    filters: SearchFilters,
    limit = 20,
    offset = 0,
  ): Promise<SearchResponse> => {
    const params = new URLSearchParams();
    params.set('q', query);

    if (filters.modules.length > 0 && !filters.modules.includes('all')) {
      params.set('modules', filters.modules.join(','));
    }
    if (filters.entityTypes.length > 0) {
      params.set('entity_types', filters.entityTypes.join(','));
    }
    if (filters.dateRange?.from) {
      params.set('from', filters.dateRange.from);
    }
    if (filters.dateRange?.to) {
      params.set('to', filters.dateRange.to);
    }
    params.set('limit', String(limit));
    params.set('offset', String(offset));
    // The app renders its OWN module + page matches (richer matching, scoped to
    // the modules the user can open — see shared/utils/moduleSearch.ts), so the
    // server's page pass is wasted work whose count would inflate `total_count`.
    // Other API consumers (API keys, MCP) still get pages by default.
    params.set('include_pages', 'false');

    return apiGet<SearchResponse>(`/api/v1/search?${params}`);
  },
};
